import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';
import { Reveal, Eyebrow, MaskedLine } from '../components/Reveal';
import { SERVICES } from '../data/content';

export default function Services() {
  return (
    <>
      <SEO
        title="Services | INSIGHTS STRATEGY — Consulting, Research & Advisory Capabilities"
        description="Management consulting, market research, policy advisory, technology research, infrastructure advisory and capacity building — evidence-based capabilities from INSIGHTS STRATEGY, Kolkata."
        path="/services"
      />
      <section className="relative overflow-hidden bg-brand-deep" data-testid="services-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(23,107,58,0.18),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-44 lg:px-8 lg:pb-28">
          <Eyebrow dark>Our Capabilities</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="services-headline">
            <MaskedLine delay={0.15}>Capabilities That Turn</MaskedLine>
            <MaskedLine delay={0.3}>Evidence Into <span className="text-brand-green">Action.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
              We combine research, analytical expertise, and strategic advisory capabilities to help organisations identify opportunities, manage complexity, and achieve meaningful outcomes.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-offwhite">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <div className="space-y-8">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const flip = i % 2 === 1;
              return (
                <Reveal key={s.slug}>
                  <article
                    id={s.slug}
                    data-testid={`service-section-${s.slug}`}
                    className={`grid scroll-mt-32 gap-10 rounded-3xl border border-brand-grey bg-white p-8 sm:p-12 lg:grid-cols-12 ${i === 0 ? '' : ''}`}
                  >
                    <div className={`lg:col-span-5 ${flip ? 'lg:order-2' : ''}`}>
                      <div className="flex items-center gap-4">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-deep text-brand-green">
                          <Icon size={26} strokeWidth={1.6} />
                        </div>
                        <span className="font-heading text-sm font-extrabold text-brand-green">0{i + 1}</span>
                      </div>
                      <h2 className="mt-6 font-heading text-2xl font-bold leading-tight tracking-tight text-brand-black sm:text-3xl">{s.title}</h2>
                      <p className="mt-4 text-base leading-relaxed text-brand-body">{s.about}</p>
                      <div className="mt-6">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-deepgreen">Example applications</p>
                        <ul className="mt-3 space-y-2">
                          {s.applications.map((a) => (
                            <li key={a} className="flex items-start gap-2 text-sm text-brand-body">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-green" />
                              {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Link
                        to={`/contact?service=${s.slug}`}
                        data-testid={`service-enquiry-${s.slug}`}
                        className="group mt-8 inline-flex items-center gap-2 rounded-full border border-brand-black/15 px-6 py-3 font-heading text-sm font-bold text-brand-black transition-all duration-300 hover:border-brand-green hover:bg-brand-green hover:text-white"
                      >
                        Discuss this capability
                        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </div>
                    <div className={`lg:col-span-7 ${flip ? 'lg:order-1' : ''}`}>
                      <div className="grid h-full content-start gap-x-8 gap-y-3 rounded-2xl bg-brand-offwhite p-7 sm:grid-cols-2 sm:p-9">
                        {s.offerings.map((o) => (
                          <div key={o} className="flex items-start gap-3">
                            <Check size={16} className="mt-0.5 shrink-0 text-brand-green" strokeWidth={3} />
                            <span className="text-sm leading-relaxed text-brand-black/80">{o}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep" data-testid="services-cta">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-5 py-20 lg:px-8">
          <div>
            <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">Not sure which capability fits your challenge?</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/60">Tell us what you are trying to achieve — we will help you shape the right research or advisory approach.</p>
          </div>
          <Link to="/contact" data-testid="services-cta-contact" className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-4 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-deepgreen">
            Let's Talk <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
}
